/**
 * Layer 2: Payload Condition Check
 * 
 * Validates that the GrowthBook payload doesn't have explicit tenant exclusions.
 * This catches cases where the dashboard has tenant targeting rules that
 * exclude the current tenant.
 * 
 * This is the second line of defense against dashboard misconfigurations.
 */

import { AllowedExperimentKey, EXPERIMENT_FEATURE_KEY, TenantId } from '@tenants/types';
import { ExperimentObservabilityService } from 'observability/services/experiment-observability';
import { ExperimentContext } from 'observability/constants/experiment-events';
import type { GrowthBookPayload } from '@growthbook/growthbook-react';
import type { CircuitBreakerResult } from './types';
import { CircuitBreakerLayer } from './types';

/**
 * Check if GrowthBook payload has explicit tenant exclusions.
 * 
 * This is the second circuit breaker layer that validates the GrowthBook
 * payload doesn't have explicit tenant conditions that exclude the current tenant.
 * 
 * @param experimentKey - The experiment to check
 * @param payload - The GrowthBook payload from the SDK
 * @param tenantId - The tenant ID to check against
 * @param context - Whether this is SSR or client-side evaluation
 * @returns CircuitBreakerResult if blocked, null if allowed to continue to next layer
 */
export function checkPayloadCondition(
  experimentKey: AllowedExperimentKey,
  payload: GrowthBookPayload | undefined,
  tenantId: TenantId | undefined,
  context: ExperimentContext
): CircuitBreakerResult | null {
  const feature = payload?.features?.[EXPERIMENT_FEATURE_KEY[experimentKey]];
  
  if (!feature?.rules) {
    // No rules, continue to next layer
    return null;
  }

  // Check if any rule explicitly excludes this tenant
  const hasExplicitTenantCondition = feature.rules.some((rule: any) => 
    rule.condition?.tenantId && rule.condition.tenantId !== tenantId
  );
  
  if (hasExplicitTenantCondition) {
    // Track the block
    ExperimentObservabilityService.trackPayloadConditionBlocked({
      experimentKey,
      context,
      tenantId,
      payloadConditions: feature.rules.map((r: any) => r.condition).filter(Boolean),
    });

    // Return blocked result
    return {
      allowed: false,
      variationId: 0,
      layer: CircuitBreakerLayer.PAYLOAD_CONDITION,
      reason: 'GrowthBook payload has explicit tenant exclusion',
      isEnabledForTenant: true,
    };
  }

  // Allowed - continue to next layer
  return null;
}
